import { FC, useState, useEffect, useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import classNames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { SpeakerMuteIcon } from '@/atoms/Icons/SpeakerMuteIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { ParagraphSM } from '@/atoms/Text'
import { BoundedImage } from '@/molecules/BoundedImage'
import { useGuildMember } from '@/services/GuildUserService'
import { FlowPhaseForStatusCheck, isInGuildAccess, isSellingTickets, isStreaming } from '@/services/PhaseManagerService'
import { ProjectHoverContent, UnifiedProjectHoverContent } from '@/services/ProjectsService/types'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeAnalytics } from '@/utils/analytics'
import { BitAngelPlayer } from '../BitAngelPlayer'
import { PreviewVideoPlayerUI } from '../BitAngelPlayer/PreviewVideoPlayerUI'
import { WatchProgressStatus } from '../WatchProjectHero/WatchProgressStatus'
import { ContentMetadata } from './ContentMetadata'
import { CtaBox } from './CtaBox'
import { CtaButtons } from './CtaButtons'
import { useHoverContent, useModalVisibility, useVideoPlayer } from './hooks'

interface WatchHoverModalProps {
  className?: string
  guid?: string | null
  projectSlug?: string | null
  isVisible: boolean
}

export const WatchHoverModal: FC<WatchHoverModalProps> = ({ className, guid, isVisible, projectSlug }) => {
  const client = useApolloClient()
  const { isGuildMember, guildPermissions } = useGuildMember()
  const { isLoggedIn } = useUser()
  const { track } = useSafeAnalytics()
  const [isMounted, setIsMounted] = useState(false)

  const { hoverContent, isLoading } = useHoverContent({ isVisible, guid, projectSlug, client })
  const { shouldShow, setShouldShow } = useModalVisibility(isVisible)
  const { playerControls, isPlayerMuted, playerKey, handleUnmute, handlePlayerReady } = useVideoPlayer(isVisible)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const modalVariants = {
    hidden: {
      opacity: 0,
      y: '100%',
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      y: '100%',
      transition: {
        duration: 0.2,
        ease: 'easeIn',
      },
    },
  }

  const handleModalClose = useCallback(() => {
    track('Watch Hover Modal Closed')
    setShouldShow(false)
  }, [setShouldShow, track])

  const flowPhases = getFlowPhases(hoverContent)
  const isInTheaters = isSellingTickets(flowPhases ?? [])
  const currentlyStreaming = getIsCurrentlyStreaming(flowPhases, hoverContent)
  const isGuildGated = isInGuildAccess(flowPhases ?? [])
  const hasFreeTickets = guildPermissions?.hasFreeTickets ?? false
  const canSeeGuildShareButton = hoverContent?.isGuildShareAvailable ?? false
  const shareCtaPath = getShareCtaPath(hoverContent)
  const { position, duration, isDryBarTitle } = getContentMetadata(hoverContent)

  if (!isMounted) {
    return null
  }

  //TODO add translations for this component if this wins
  const modalContent = (
    <AnimatePresence>
      {shouldShow && hoverContent && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleModalClose}
          />

          {/* Modal */}
          <motion.div
            className={classNames(
              'fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-core-gray-925 border-t border-white/10 shadow-2xl min-h-[60vh] lg:min-w-[972px] max-w-xl mx-auto',
              className,
            )}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex flex-col overflow-hidden rounded-t-2xl">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="h-6 animate-pulse rounded bg-white/10" />
                  <div className="h-4 w-3/4 animate-pulse rounded bg-white/10" />
                  <div className="h-16 animate-pulse rounded bg-white/10" />
                </div>
              ) : hoverContent ? (
                <>
                  <div className="relative overflow-hidden">
                    {hoverContent?.preview?.m3u8Uri ? (
                      <div className="absolute inset-0 min-h-[547px] w-full overflow-hidden rounded-t-2xl shortDesktop:min-h-[400px]">
                        <BitAngelPlayer
                          key={playerKey}
                          autoplay
                          hideFullscreen
                          muted
                          className="aspect-h-9 aspect-w-16 overflow-hidden"
                          ui={PreviewVideoPlayerUI}
                          url={hoverContent.preview.m3u8Uri ?? ''}
                          onStart={() => {}}
                          onPlaybackFinished={() => {}}
                          onPlayerInit={handlePlayerReady}
                        />
                        {isPlayerMuted && playerControls && (
                          <Button
                            variant="plain"
                            className="absolute bottom-10 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full border-transparent bg-white/20 p-2 backdrop-blur-xl transition-all duration-200 ease-in-out hover:bg-white/10"
                            onClick={handleUnmute}
                            aria-label="Unmute video"
                          >
                            <SpeakerMuteIcon size={24} color="core-gray-500" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <ResponsiveBackgroundImage
                        className="min-h-[547px] shortDesktop:min-h-[400px]"
                        src={getCloudinaryImageUrl({
                          path: getBackgroundImagePath(hoverContent, isDryBarTitle),
                        })}
                      />
                    )}
                    <LinearGradient />
                    <div className="relative flex min-h-[547px] flex-col items-start justify-end shortDesktop:min-h-[400px]">
                      <Button
                        variant="plain"
                        className="absolute right-4 top-4 z-50 flex h-8 w-8 items-center justify-center rounded-full border-transparent bg-core-gray-200 p-1.5 shadow-2xl transition-all duration-200 ease-in-out hover:bg-core-gray-400 active:opacity-75"
                        onClick={handleModalClose}
                        aria-label="Close Hover"
                      >
                        <CloseIcon size={32} color="black" />
                      </Button>
                      <div className="relative mx-12 w-full">
                        {(() => {
                          const logoPath = getLogoPath(hoverContent, isDryBarTitle)

                          if (!logoPath) {
                            return null
                          }

                          return (
                            <BoundedImage
                              className="mb-2 flex items-center justify-center md:justify-start"
                              alt={hoverContent?.name || ''}
                              path={logoPath}
                              width={600}
                              height={104}
                            />
                          )
                        })()}
                        <CtaButtons
                          hoverContent={hoverContent}
                          isGuildMember={isGuildMember}
                          isInTheaters={isInTheaters}
                          currentlyStreaming={currentlyStreaming}
                          isGuildGated={isGuildGated}
                          canSeeGuildShareButton={canSeeGuildShareButton}
                          shareCtaPath={shareCtaPath}
                          isLoggedIn={isLoggedIn}
                        />
                        <WatchProgressStatus
                          position={position ?? undefined}
                          duration={duration ?? undefined}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mx-12 flex flex-col flex-wrap items-center justify-start gap-2 !text-core-gray-400 md:mb-6 md:items-start">
                    <ContentMetadata hoverContent={hoverContent} />
                    <div className="flex flex-col gap-4 pt-4 lg:flex-row lg:gap-6">
                      <ParagraphSM className="lg:photon-paragraph-md shortDesktop:photon-caption-md">
                        {getContentDescription(hoverContent, isDryBarTitle)}
                      </ParagraphSM>
                      {!isDryBarTitle && (
                        <CtaBox
                          hoverContent={hoverContent}
                          isGuildMember={isGuildMember}
                          isInTheaters={isInTheaters}
                          currentlyStreaming={currentlyStreaming}
                          isGuildGated={isGuildGated}
                          hasFreeTickets={hasFreeTickets}
                          canSeeGuildShareButton={canSeeGuildShareButton}
                          shareCtaPath={shareCtaPath}
                        />
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )

  return createPortal(modalContent, document.body)
}

const LinearGradient = ({ className }: { className?: string }) => (
  <div
    className={classNames('absolute bottom-0 left-0 h-full w-full', className)}
    style={{ background: 'linear-gradient(0deg, #1D1D1D 0%, rgba(29, 29, 29, 0.00) 100%)' }}
  />
)

const getShareCtaPath = (hoverContent: ProjectHoverContent | null): string | null => {
  if (!hoverContent || !hoverContent?.project?.slug || !hoverContent.id) return null

  if (
    hoverContent.__typename === 'ContentSeries' &&
    hoverContent?.firstWatchable &&
    'id' in hoverContent.firstWatchable &&
    typeof hoverContent.firstWatchable.id === 'string'
  ) {
    return `/watch/${hoverContent.project.slug}/episode/${hoverContent.firstWatchable.id}?share=true`
  }
  return `/watch/${hoverContent.project.slug}/episode/${hoverContent.id}?share=true`
}

const getFlowPhases = (hoverContent: UnifiedProjectHoverContent | null): FlowPhaseForStatusCheck[] => {
  if (!hoverContent || !('project' in hoverContent) || !hoverContent?.project?.primaryFlowPhases) {
    return []
  }

  const phases = hoverContent.project.primaryFlowPhases
  return Array.isArray(phases) ? phases.filter((phase): phase is FlowPhaseForStatusCheck => !!phase) : []
}

const getLogoPath = (hoverContent: UnifiedProjectHoverContent | null, isDryBarTitle: boolean): string => {
  if (isDryBarTitle) {
    return '/v1739221751/studio-app/catalog/d026ed61-de35-440c-9a02-af49fcf35e4c'
  }

  return (
    hoverContent?.logo?.cloudinaryPath ||
    (hoverContent &&
      'project' in hoverContent &&
      hoverContent.project &&
      'logoCloudinaryPath' in hoverContent.project &&
      hoverContent.project.logoCloudinaryPath) ||
    ''
  )
}

const getBackgroundImagePath = (hoverContent: UnifiedProjectHoverContent | null, isDryBarTitle: boolean): string => {
  if (isDryBarTitle) {
    return '/v1668465113/angel-app/temp-discover/dry-bar-best-of-landscape'
  }

  return (
    hoverContent?.still3?.cloudinaryPath ??
    hoverContent?.still2?.cloudinaryPath ??
    hoverContent?.still1?.cloudinaryPath ??
    hoverContent?.nonTitleArt?.cloudinaryPath ??
    hoverContent?.backdrop?.cloudinaryPath ??
    ''
  )
}

const getContentDescription = (hoverContent: UnifiedProjectHoverContent | null, isDryBarTitle: boolean): string => {
  if (isDryBarTitle) {
    return `Dry Bar Comedy delivers hilarious, clean humor you can enjoy with anyone—kids, grandparents, or even your most easily offended friends. With nearly 6 billion views and hundreds of comedians, it's proving comedy doesn't need to be dirty to be funny.`
  }

  return hoverContent?.longDescription ?? hoverContent?.mediumDescription ?? hoverContent?.shortDescription ?? ''
}

const getContentMetadata = (hoverContent: UnifiedProjectHoverContent | null) => {
  const position = hoverContent && 'watchPosition' in hoverContent ? hoverContent?.watchPosition?.position : 0
  const duration = hoverContent && 'duration' in hoverContent ? hoverContent.duration : 0
  const isDryBarTitle =
    hoverContent?.__typename === 'ContentSeries' && hoverContent?.project?.slug === 'dry-bar-best-of'

  return { position, duration, isDryBarTitle }
}

const getIsCurrentlyStreaming = (
  flowPhases: FlowPhaseForStatusCheck[],
  hoverContent: UnifiedProjectHoverContent | null,
): boolean => {
  return !!(
    isStreaming(flowPhases ?? []) ||
    (hoverContent &&
      'watchableAvailabilityStatus' in hoverContent &&
      (hoverContent?.watchableAvailabilityStatus === 'WATCHABLE' ||
        hoverContent?.watchableAvailabilityStatus === 'ACTION_REQUIRED'))
  )
}
